/**
 * 통합 오버레이 컴포넌트
 * 카카오맵 CustomOverlayMap 또는 Leaflet DivIcon Marker를 자동으로 선택
 */

import { ReactNode, useEffect, useRef, cloneElement, isValidElement } from "react";
import { CustomOverlayMap as KakaoOverlay } from "react-kakao-maps-sdk";
import { Marker, Popup } from "react-leaflet";
import L, { divIcon } from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { useCurrentMapProvider } from "./MapProviderContext";
import type { Marker as LeafletMarker } from "leaflet";

export interface UnifiedOverlayProps {
  position: { lat: number; lng: number };
  clickable?: boolean;
  yAnchor?: number;
  xAnchor?: number;
  zIndex?: number;
  children: ReactNode;
  onClick?: () => void;
  interactive?: boolean; // true일 때 React 컨텍스트 유지 (Popup 사용)
  variant?: 'default' | 'cluster'; // cluster일 때만 클러스터 스타일 적용
}

/**
 * 통합 오버레이 컴포넌트
 * 카카오맵의 CustomOverlayMap과 동일한 인터페이스 제공
 */
// Interactive Popup을 자동으로 여는 컴포넌트
const InteractivePopupMarker: React.FC<{
  position: { lat: number; lng: number };
  zIndex?: number;
  children: ReactNode;
}> = ({ position, zIndex, children }) => {
  const markerRef = useRef<LeafletMarker>(null);
  
  useEffect(() => {
    // 마커가 생성되면 Popup을 자동으로 열기
    const timer = setTimeout(() => {
      if (markerRef.current) {
        markerRef.current.openPopup();
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [position]);
  
  // 투명한 작은 마커 생성
  let invisibleIcon: L.DivIcon | undefined;
  try {
    invisibleIcon = divIcon({
      html: '',
      className: 'invisible-marker',
      iconSize: [1, 1],
    });
  } catch (error) {
    console.error("Failed to create invisible icon:", error);
    invisibleIcon = undefined;
  }
  
  // 마커 이벤트 핸들러 - add 이벤트에서 Popup 열기
  const eventHandlers = {
    add: () => {
      if (markerRef.current) {
        markerRef.current.openPopup();
      }
    }
  };
  
  return (
    <Marker
      ref={markerRef}
      position={[position.lat, position.lng]}
      icon={invisibleIcon}
      zIndexOffset={zIndex || 1000}
      eventHandlers={eventHandlers}
    >
      <Popup
        closeButton={false}
        autoClose={false}
        closeOnClick={false}
        autoPan={false}
        maxWidth={500}
        minWidth={300}
        offset={[0, -80]}
      >
        {children}
      </Popup>
    </Marker>
  );
};

export const UnifiedOverlay: React.FC<UnifiedOverlayProps> = ({
  position,
  clickable = true,
  yAnchor,
  xAnchor,
  zIndex,
  children,
  onClick,
  interactive = false,
  variant = 'default',
}) => {
  const mapProvider = useCurrentMapProvider();

  if (mapProvider === "osm") {
    // interactive 모드일 때는 Popup 사용 (React 컨텍스트 유지)
    if (interactive) {
      return <InteractivePopupMarker position={position} zIndex={zIndex}>{children}</InteractivePopupMarker>;
    }
    
    // OSM 일반 모드: variant에 따라 처리 분기
    let htmlContent: string;
    let iconSize: [number, number] | undefined;
    let iconAnchor: [number, number] | undefined;

    if (variant === 'cluster') {
      // 카카오맵 클러스터 스타일 그대로 적용
      const styledChildren = (
        <div
          style={{
            cursor: 'pointer',
            position: 'relative',
            width: '50px',
            height: '50px',
            textAlign: 'center',
            color: '#ffffff',
            fontWeight: 'bold',
            fontSize: 'large',
            padding: '10px',
            borderRadius: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'radial-gradient(circle farthest-corner at center center, #25863F 40%, rgba(37, 134, 63, 0) 100%)',
          }}
        >
          <div
            style={{
              background: '#25863F',
              width: '30px',
              height: '30px',
              borderRadius: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: 0,
              padding: 0,
              color: '#ffffff',
            }}
          >
            {children}
          </div>
        </div>
      );
      htmlContent = renderToStaticMarkup(styledChildren);
      iconSize = [50, 50];
      iconAnchor = [25, 25];
    } else {
      // 클러스터가 아닌 경우: children을 그대로 렌더 (배경/그라데이션 미적용)
      htmlContent = renderToStaticMarkup(<div style={{ margin: 0, padding: 0 }}>{children}</div>);
      iconSize = undefined; // 자식이 자체 크기를 갖도록 둠
      iconAnchor = [0, 0]; // 자식이 자체 위치 계산(커스텀 마커에서 translate로 보정)
    }

    let customIcon: L.DivIcon | undefined;
    try {
      customIcon = divIcon({
        html: htmlContent,
        className: 'custom-div-icon',
        iconSize,
        iconAnchor,
      });
    } catch (error) {
      console.error("Failed to create divIcon:", error);
      // 기본 아이콘 사용
      if (typeof window !== "undefined" && L && L.Icon && L.Icon.Default) {
        try {
          customIcon = new L.Icon.Default() as any;
        } catch (defaultError) {
          console.error("Failed to create default icon:", defaultError);
          customIcon = undefined;
        }
      }
    }

    // onClick이 있을 때만 eventHandlers 추가
    const eventHandlers = onClick ? { click: onClick } : undefined;

    return (
      <Marker
        position={[position.lat, position.lng]}
        icon={customIcon}
        zIndexOffset={zIndex}
        eventHandlers={eventHandlers}
      />
    );
  }

  // 카카오맵 CustomOverlayMap
  if (variant === 'cluster') {
    // 클러스터일 때는 카카오맵에서도 동일한 스타일 적용
    const styledChildren = (
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) {
            onClick();
          }
        }}
        style={{
          cursor: 'pointer',
          position: 'absolute',
          width: '50px',
          height: '50px',
          textAlign: 'center',
          color: '#ffffff',
          fontWeight: 'bold',
          fontSize: 'large',
          padding: '10px',
          borderRadius: '100%',
          top: '-30px',
          right: '-30px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'radial-gradient(circle farthest-corner at center center, #25863F 40%, rgba(37, 134, 63, 0) 100%)',
        }}
      >
        <div
          style={{
            background: '#25863F',
            width: '30px',
            height: '30px',
            borderRadius: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
            padding: 0,
            color: '#ffffff',
          }}
        >
          {children}
        </div>
      </div>
    );

    return (
      <KakaoOverlay
        position={position}
        clickable={clickable}
        yAnchor={yAnchor}
        xAnchor={xAnchor}
        zIndex={zIndex}
      >
        {styledChildren}
      </KakaoOverlay>
    );
  }

  // 일반 오버레이
  const childrenWithClick = onClick && isValidElement(children)
    ? cloneElement(children as React.ReactElement, { 
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          onClick();
        },
        style: { ...((children as React.ReactElement).props.style), cursor: 'pointer' }
      })
    : children;
  
  return (
    <KakaoOverlay
      position={position}
      clickable={clickable}
      yAnchor={yAnchor}
      xAnchor={xAnchor}
      zIndex={zIndex}
    >
      {childrenWithClick}
    </KakaoOverlay>
  );
};

